/**
 * Messages handed to the AI panels (assistant, summary, action items, draft reply).
 *
 * Keeps normal text messages and the Eurion Facilitator's meeting recaps (type
 * "system", metadata.kind "meeting_recap"), so the assistant can build on what a
 * meeting decided. Rich-text (HTML) content is reduced to plain text.
 */

/** messaging-service FACILITATOR_SENDER_ID — the only sender of trusted recaps */
export const FACILITATOR_SENDER_ID = '00000000-0000-4000-8000-00000000fac1';
export const FACILITATOR_NAME = 'Eurion Facilitator';

export interface AiContextSource {
  senderId: string;
  content: string;
  type?: string;
  format?: string;
  isDeleted?: boolean;
  metadata?: Record<string, unknown>;
}

export function isMeetingRecap(m: AiContextSource): boolean {
  return m.type === 'system' && m.metadata?.['kind'] === 'meeting_recap' && m.senderId === FACILITATOR_SENDER_ID;
}

/** HTML → readable plain text: block ends and list items become new lines. */
export function htmlToText(html: string): string {
  return html
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\s*li[^>]*>/gi, '\n- ')
    .replace(/<\/\s*(p|li|ul|ol|h[1-6]|blockquote)\s*>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function aiContextMessages(
  messages: AiContextSource[],
  getSenderName: (senderId: string) => string,
): { sender: string; content: string }[] {
  return messages
    .filter(m => !m.isDeleted && (m.type === 'text' || isMeetingRecap(m)))
    .map(m => ({
      sender: isMeetingRecap(m) ? FACILITATOR_NAME : getSenderName(m.senderId),
      content: m.format === 'markdown' || m.format === 'html' ? htmlToText(m.content) : m.content,
    }));
}

const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Recap content as HTML for display. Recaps are HTML since e498dd2; older ones were
 * markdown (**bold**, _italic_, "- " lists), which this converts. Callers still sanitise.
 */
export function recapToHtml(content: string): string {
  if (/<\/?(p|ul|li|strong|em|br)\b/i.test(content)) return content;
  const inline = (s: string) => escapeHtml(s)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|\s)_(.+?)_(?=\s|$|[.,;:!?])/g, '$1<em>$2</em>');
  return content.trim().split(/\n{2,}/).map((block) => {
    const lines = block.split('\n');
    if (lines.every((l) => /^\s*[-*] /.test(l))) {
      return `<ul>${lines.map((l) => `<li>${inline(l.replace(/^\s*[-*] /, ''))}</li>`).join('')}</ul>`;
    }
    return `<p>${lines.map(inline).join('<br>')}</p>`;
  }).join('');
}
